import type { Variant } from '@ordering/types/Product';
import { OrderLine } from '@ordering/types/Order';
import { ProductVariantWithoutModifiers, ProductModifier } from '@ordering/gql/graphql';

export type Inventory = Record<string, number>;

export type CanAddVariant = {
  variant: Variant;
  existingOrderLineId?: string | null;
  basket: OrderLine[];
  quantityToAdd: number;
};

export type CanAddProduct = {
  variants: Variant[];
  basket?: OrderLine[];
};

export type CanAddModifier = {
  modifierVariant: ProductVariantWithoutModifiers;
};

export type CanAddVariantWithModifiers = CanAddVariant & {
  modifiers?: ProductModifier[];
};

export type ProductVariantWithModifiers = Variant & {
  modifiers?: ProductModifier[];
};

export const getVariantTotalInventory = (variant: Variant, quantity: number): Inventory => {
  const inventoryComponentToBeUsed: Inventory = {};

  if (!variant.inventoryComponents) return inventoryComponentToBeUsed;

  variant.inventoryComponents.forEach((component) => {
    const currentValue = inventoryComponentToBeUsed[component.componentId] || 0;
    inventoryComponentToBeUsed[component.componentId] = currentValue + component.quantifier * quantity;
  });

  return inventoryComponentToBeUsed;
};

export const getOrderLineTotalInventory = (orderLine: OrderLine): Inventory => {
  const productIdToInventoryToBeUsed: Inventory = {};

  if (!orderLine.variant.inventoryComponents) return productIdToInventoryToBeUsed;

  orderLine.variant.inventoryComponents.forEach((component) => {
    const currentValue = productIdToInventoryToBeUsed[component.componentId] || 0;
    productIdToInventoryToBeUsed[component.componentId] = currentValue + component.quantifier * orderLine.quantity;
  });

  return productIdToInventoryToBeUsed;
};

export const filteredInventoryByCurrentBasket = (
  basket: OrderLine[],
  orderLineId: string | null = null,
  inventory: Inventory | null
): Inventory => {
  if (!inventory || !basket) return {};

  const inventoryOnHand = { ...inventory };

  const filteredBasket = basket.filter((ol) => ol.id !== orderLineId);

  filteredBasket.forEach((orderLine) => {
    const orderLineInventory = getOrderLineTotalInventory(orderLine);

    Object.entries(orderLineInventory).forEach(([componentId, quantity]) => {
      if (inventoryOnHand[componentId] !== undefined) {
        inventoryOnHand[componentId] -= quantity;
      }
    });
  });

  return inventoryOnHand;
};

export function parseProductUrn(
  urn: string,
): { posConnectionId: string; variantId: string } | null {
  const regex = /^urn:pos:([\w-]+):(?:.*:modifier:([\w-]+)|[^:]+:(.+))$/;
  const match = urn.match(regex);

  if (!match) {
    return null;
  }

  const [, posConnectionId, modifierId, productId] = match;

  const variantId = modifierId ?? productId;

  if (!posConnectionId || !variantId) {
    return null;
  }

  return {
    posConnectionId,
    variantId,
  };
}

export const canAddModifier = ({ modifierVariant, inventory }: CanAddModifier & { inventory: Inventory | null }): boolean => {
  if (!inventory) return true;
  
  if (modifierVariant.inventoryComponents && modifierVariant.inventoryComponents.length > 0) {
    for (const component of modifierVariant.inventoryComponents) {
      const availableQuantity = inventory[component.componentId];
      
      if (availableQuantity !== undefined && availableQuantity <= 0) {
        return false;
      }
    }
    return true;
  }
  
  const parsedUrn = parseProductUrn(modifierVariant.id);
  if (parsedUrn) {
    const { variantId, posConnectionId } = parsedUrn;
    if (variantId) {
      const modifierInventory = inventory[`urn:inv:temp_oos:${variantId}:pos:${posConnectionId}`];
      
      if (modifierInventory !== undefined) {
        return modifierInventory > 0;
      }
    }
  }
  
  return true;
};

export const isModifierVariantAvailable = (modifierVariant: ProductVariantWithoutModifiers, inventory: Inventory | null, basket: OrderLine[] = []): boolean => {
  return canAddModifier({
    modifierVariant,
    inventory
  });
};

export const isVariantOutOfStockDueToModifiers = (
  modifiers: ProductModifier[] = [],
  inventory: Inventory | null,
  basket: OrderLine[] = []
): boolean => {
  if (!modifiers || modifiers.length === 0) return false;

  return modifiers.some((modifier) => {
    const minQuantity = modifier.minQuantity ?? 0;
    if (minQuantity <= 0) return false;

    const allVariantsOutOfStock = modifier.variants.every((modifierVariant) => {
      return !isModifierVariantAvailable(modifierVariant, inventory, basket);
    });

    return allVariantsOutOfStock;
  });
};

export const canAddProductVariantWithModifiers = (
  variant: ProductVariantWithModifiers,
  inventory: Inventory | null,
  basket: OrderLine[],
  quantityToAdd: number = 1,
  existingOrderLineId: string | null = null
): boolean => {
  if (!inventory) return true;

  const inventoryOnHand = filteredInventoryByCurrentBasket(basket, existingOrderLineId, inventory);
  const inventoryNeeded = getVariantTotalInventory(variant, quantityToAdd);

  for (const [componentId, quantityNeeded] of Object.entries(inventoryNeeded)) {
    const availableQuantity = inventoryOnHand[componentId];
    if (availableQuantity !== undefined && availableQuantity < quantityNeeded) {
      return false;
    }
  }

  if (variant.modifiers && isVariantOutOfStockDueToModifiers(variant.modifiers, inventory, basket)) {
    return false;
  }

  return true;
};

export const canAddProductWithModifiers = (
  variants: ProductVariantWithModifiers[],
  inventory: Inventory | null,
  basket: OrderLine[] = []
): boolean => {
  if (!inventory || variants.length === 0) return true;

  return variants.some((variant) =>
    canAddProductVariantWithModifiers(variant, inventory, basket, 1, null)
  );
};
